import { Box, Card, Grid, Paper, Typography } from "@mui/material";
import { useState, type FC } from "react";
import type { PackageResponse } from "../../admin/packages/packagesApi";
import {
  storageUrlWithCredentials,
  useStorageUrlWithCredentials,
} from "../../components/imagesPreview/storageUrlWithCredentials";
import type { Product } from "../../components/productsTable/ProductsTableNew";
import ProductsTable from "../../components/productsTable/ProductsTableNew";
import type { ECommerceOfferResponse } from "../../eCommerce/eCommerceApi";
import DownloadOrderPdfButton from "../../orders/DownloadOrderPdfButton";
import { getTotalPrice } from "../../orders/orderEdit/getTotalPrice";
import { CardMediaImg } from "../../publicPackages2/CardImage";
import { LightboxDialog } from "../../publicPackages2/LightboxDialog";
import { useProductDateRangeLabel } from "../productDateRangeLabel";
import { BookOfferButton } from "./BookOfferButton";
import { ECommerceActions } from "./ECommerceActions";
import ECommerceOfferHeader from "./ECommerceOfferHeader";
import ECommerceOfferSubheader from "./ECommerceOfferSubheader";
import OtherOffers from "./OtherOffers";
import PackageTile from "./PackageTile";
import type { EcommerceProductWithOptions } from "./useECommerceOfferToProducts";

interface Props {
  readonly offer: ECommerceOfferResponse;
  readonly packageData: PackageResponse;
  readonly products: EcommerceProductWithOptions[];
  readonly onBack: () => void;
}

const ECommerceOfferDetails: FC<Props> = ({ offer, onBack, packageData, products }) => {
  const [lightboxImage, setLightboxImage] = useState<string>();

  const totalPrice = getTotalPrice(products);
  const people = offer.participantsNumber.numberOfPaid + offer.participantsNumber.numberOfStaff;

  const tripDateRange = useProductDateRangeLabel(products);

  const url = useStorageUrlWithCredentials(packageData?.media?.images[0]?.www.url);
  const mapThumbnailUrl = packageData?.media?.map?.thumbnail.url;
  return (
    <Box sx={{ animation: "fadeIn 0.4s ease-out", pb: 8 }}>
      <ECommerceActions offer={offer} format={"description"} onBack={onBack} />

      <Card
        sx={{ mb: 4, borderRadius: 1, overflow: "hidden", border: "none", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}
      >
        <ECommerceOfferHeader offer={offer} tripDateRange={tripDateRange} url={url ?? null} />
        <ECommerceOfferSubheader offer={offer} packageData={packageData} />
      </Card>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "8fr 4fr" },
          gap: 4,
          alignItems: "start",
        }}
      >
        <ProductsTable
          values={products as Product[]}
          currency={offer.currency}
          totalPrice={totalPrice}
          hideProductPrices={false}
          orderId={offer.id.value}
          files={offer.files}
        />
        <Box>
          <Box sx={{ position: "sticky", top: 100 }}>
            {mapThumbnailUrl && (
              <Paper
                elevation={0}
                sx={{ p: 1, borderRadius: 1, border: "1px solid rgba(0,0,0,0.08)", mb: 3, cursor: "pointer" }}
                onClick={() =>
                  storageUrlWithCredentials(packageData?.media?.map?.www.url).then((url) => {
                    if (url) {
                      setLightboxImage(url);
                    }
                  })
                }
              >
                <CardMediaImg
                  imageUrl={mapThumbnailUrl}
                  alt="Mapa wycieczki"
                  sx={{ width: "100%", height: "auto", display: "block", borderRadius: 1 }}
                />
              </Paper>
            )}

            {packageData && <PackageTile packageData={packageData} url={url} />}

            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                borderRadius: 1,
                border: "1px solid rgba(13, 110, 253, 0.18)",
                bgcolor: "rgba(13, 110, 253, 0.04)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="subtitle2"
                color="textSecondary"
                sx={{ mb: 2.5, letterSpacing: 1, fontWeight: "bold", textTransform: "uppercase" }}
              >
                Uczestnicy
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                    Osoby płatne
                  </Typography>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: "bold" }}>
                    {offer.participantsNumber.numberOfPaid || "-"}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                    Obsługa
                  </Typography>
                  <Typography variant="h4" color="warning" sx={{ fontWeight: "bold" }}>
                    {offer.participantsNumber.numberOfStaff}
                  </Typography>
                </Grid>
              </Grid>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                sx={{ mt: 2.5, mb: 1, letterSpacing: 1, fontWeight: "bold", textTransform: "uppercase" }}
              >
                Cena oferty e-commerce dla {people} osób
              </Typography>
              <Typography variant="h4" color="primary" sx={{ fontWeight: "bold" }}>
                {totalPrice.toDisplayValue(offer.currency)}
              </Typography>

              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 2.5, mb: 2.5, lineHeight: 1.5, fontWeight: "bold" }}
              >
                Przy rezerwacji istnieje możliwość zmodyfikowania ilości uczestników, świadczeń i rodzajów pokoi/kabin
                zgodnie z wielkością i preferencjami grupy. Liczba osób płatnych i obsługi może zostać dopasowana do
                potrzeb wyjazdu, zgłoś uwagi po kliknięciu &quot;Rezerwuj&quot;.
              </Typography>
              <BookOfferButton
                fullWidth
                variant="contained"
                color="primary"
                disableElevation
                sx={{ borderRadius: 2, mb: 1.5 }}
                offer={offer}
              />
              <DownloadOrderPdfButton orderId={offer.id.value} filename={`${offer.name}.pdf`} />
            </Paper>
          </Box>
        </Box>
      </Box>
      <OtherOffers mainOffer={offer} />

      <LightboxDialog imageUrl={lightboxImage} onClose={() => setLightboxImage(undefined)} />
    </Box>
  );
};

export default ECommerceOfferDetails;
